import { z } from 'zod';
import { EXTERNAL_SYSTEMS, ExternalSystemSchema, LEASE_MINUTES_MAX, LEASE_MINUTES_MIN } from './systems';

export const BlockedWhySchema = z.enum(['no-lease', 'expired', 'revoked', 'ceiling', 'db-error']);
export type BlockedWhy = z.infer<typeof BlockedWhySchema>;

/** The failure reason for a Clay write the external-write gate refused, on every Clay path. */
export const EXTERNAL_WRITE_BLOCKED_REASON = 'external_write_blocked';

const LeaseActorSchema = z.object({ id: z.number().int(), name: z.string() });

const LastChangeSchema = z.object({
  at: z.string().datetime(),
  setBy: LeaseActorSchema,
  reason: z.string(),
});

export const SystemLeaseSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('live'),
    system: ExternalSystemSchema,
    liveUntil: z.string().datetime(),
    setBy: LeaseActorSchema,
    reason: z.string(),
    batchId: z.string().uuid(),
    ceiling: z.literal(false),
  }),
  z.object({
    mode: z.literal('sandbox'),
    system: ExternalSystemSchema,
    why: BlockedWhySchema,
    ceiling: z.boolean(),
    lastChange: LastChangeSchema.nullable(),
    // The end of the lease that ran out. Set only when why is 'expired'.
    liveUntil: z.string().datetime().nullable(),
  }),
]);
export type SystemLease = z.infer<typeof SystemLeaseSchema>;

export const ExternalWritesStateSchema = z.object({
  serverNow: z.string().datetime(),
  systems: z.array(SystemLeaseSchema).length(EXTERNAL_SYSTEMS.length),
  allLive: z.boolean(),
});
export type ExternalWritesState = z.infer<typeof ExternalWritesStateSchema>;

export const ExternalWritesFlipSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('live'),
    systems: z.array(ExternalSystemSchema).nonempty(),
    minutes: z.number().int().min(LEASE_MINUTES_MIN).max(LEASE_MINUTES_MAX),
    reason: z.string().trim().min(3).max(200),
  }),
  z.object({
    mode: z.literal('sandbox'),
    systems: z.array(ExternalSystemSchema).nonempty(),
    reason: z.string().trim().min(1).max(200),
  }),
]);
export type ExternalWritesFlip = z.infer<typeof ExternalWritesFlipSchema>;

export const ExternalWriteBlockedProblemSchema = z.object({
  type: z.literal('urn:terra:problem:external-write-blocked'),
  status: z.literal(423),
  title: z.string(),
  code: z.literal('EXTERNAL_WRITE_BLOCKED'),
  system: ExternalSystemSchema,
  operation: z.string(),
  why: BlockedWhySchema,
  liveUntil: z.string().datetime().nullable(),
});
export type ExternalWriteBlockedProblem = z.infer<typeof ExternalWriteBlockedProblemSchema>;
